import { Route, Routes, useLocation, useNavigate } from "react-router";
import NotFound from "./Pages/NotFound";
import ChatPage from "./Pages/ChatPage";
import PrivateRoute from "./Router/PrivateRoute";
import { Toaster } from "react-hot-toast";
import LoginPage from "./Pages/LoginPage";
import SpinnerComponent from "./Components/GenericComponents/SpinnerComponent";
import { useEffect, useState } from "react";
import ConversationContextProvider from "./Context/ConversationsContext";
import LocalStorage from "./Helpers/LocalStorage";
import OfflineView from "./Components/GenericComponents/OfflineView";
import { useOnlineStatus } from "./Helpers/useOnlineStatus";
import InitializeAnalytics from "./Components/Analytics/InitializeAnalytics";
import ReactGA from "react-ga4";
import QuickChatPage from "./Pages/QuickChatPage";
import QuickChatContextProvider from "./Context/QuickChatContext";
import QcWebSocketServiceProvider from "./Context/QuickChatWebsocketContext";
import ConferenceRegistrationPage from "./Pages/ConferenceRegistrationPage";
import { useAuthService } from "./Context/AuthContext";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { createSearchParams, useSearchParams } from "react-router-dom";
import DesktopLoginSuccess from "./Pages/DesktopLoginSuccess";
import SsoAuthPage from "./Pages/SsoAuthSuccess";
import OnboardingPage from "./Pages/OnboardingPage";
import styled, { ThemeProvider } from "styled-components";
import LogoutDeletedUser from "./Pages/LogoutDeletedUser";
import { useLinksquaredService } from "./Context/LinksquaredContext";
import { decodeJoinByCodeURL, decodeMeetingURL, isValidMeetingData } from "./Helpers/deepLinkHelper";
import {
  useGenericComponentsStateService,
} from "./Context/GenericComponentsStateContext";
import DeeplinkMeetingPickApp from "./Components/Meetings/DeeplinkMeetingPickApp";
import { lightTheme } from "./Assets/styles/lightTheme";
import { darkTheme } from "./Assets/styles/darkTheme";
import LoginForTokenPage from "./Pages/LoginForTokenPage";
import CallWrapperPage from "./Pages/CallWrapperPage";
import CallModal from "./Components/AudioVideoCalls/CallModal";
import ConferenceJoinPage from "./Pages/ConferenceJoinPage";
import ConferenceInPersonJoinPage from "./Pages/ConferenceInPersonJoinPage";
import ConferenceInPersonTranslationPage from "./Pages/ConferenceInPersonTranslationPage";
import MeetingJoinPage from "./Pages/MeetingJoinPage";
import PresentationTranslationToolPage from "./Pages/PresentationTranslationToolPage";
import VideoTranslationToolPage from "./Pages/VideoTranslationToolPage";
import { authBridgeLogin } from "./API/authBridge";
import { needsSsoOnboarding } from "./Helpers/userProfile";

function App() {
  const {
    getCurrentUserNoRedirect,
    signOutFirebase,
    user,
    getCustomToken,
    handleLogoutUser,
    disableAuthCallback,
    disableAuthObserver,
  } = useAuthService();

  const { loading, theme } = useGenericComponentsStateService();

  const [searchParams, setSearchParasm] = useSearchParams();
  const isOnline = useOnlineStatus();
  const isElectron = !!window.electron;
  const navigate = useNavigate();
  const location = useLocation();
  const hasLoginForToken = location.pathname.includes("login-for-token");
  const getShouldStayOnCurrentPage = () => {
    const hashPath = window.location.hash || location.hash || "";
    const isSsoAuthPath =
      location.pathname.includes("/sso-authentication") ||
      hashPath.includes("/sso-authentication");
    let hasPendingConferenceSignIn = false;
    try {
      hasPendingConferenceSignIn = !!sessionStorage.getItem("conference_sign_in_back_to");
    } catch (_) {}

    return (
      (location.pathname.includes("/conferences/") &&
        location.pathname.includes("/registration")) ||
      (hashPath.includes("/conferences/") &&
        hashPath.includes("/registration")) ||
      location.pathname.includes("/conferences/join") ||
      hashPath.includes("/conferences/join") ||
      location.pathname.includes("/conferences/in-person-translation") ||
      hashPath.includes("/conferences/in-person-translation") ||
      location.pathname.includes("/meetings/join") ||
      hashPath.includes("/meetings/join") ||
      (isSsoAuthPath && (!!window.electron || hasPendingConferenceSignIn))
    );
  };

  const getShouldPreventRedirect = () => {
    const hashPath = window.location.hash || location.hash || "";
    return (
      getShouldStayOnCurrentPage() ||
      location.pathname.includes("/presentation-translation-tool") ||
      hashPath.includes("/presentation-translation-tool") ||
      location.pathname.includes("/video-translation-tool") ||
      hashPath.includes("/video-translation-tool")
    );
  };

  var sso_auth = LocalStorage.getSSOFlag();

  const [meetingDataFromDeepLink, setMeetingDataFromDeepLink] = useState(null);

  const { linksquaredReceivedData, deepLink, setDeepLink } =
    useLinksquaredService(); // Get data from context

  const handleShowMeeting = (id, title, subtitle) => {
    setMeetingDataFromDeepLink({
      id: id,
      title: title,
      subtitle: subtitle,
    });
  };

  const handleDeeplink = () => {
    console.log("handle deeplink", deepLink);
    if (deepLink) {
      const joinByCodeCredentials = decodeJoinByCodeURL(deepLink);

      if (joinByCodeCredentials) {
        setDeepLink(null);
        navigate("/meetings/join", { state: joinByCodeCredentials });
        return;
      }

      const meetingDetails = decodeMeetingURL(deepLink);

      if (meetingDetails) {
        handleShowMeeting(
          meetingDetails.meeting_id,
          meetingDetails.meeting_title,
          meetingDetails.meeting_description
        );
      }
    }
  };

  const handleLinksquaredData = () => {
    if (linksquaredReceivedData) {
      if (isValidMeetingData(linksquaredReceivedData)) {
        handleShowMeeting(
          linksquaredReceivedData.meeting_id,
          linksquaredReceivedData.meeting_title,
          linksquaredReceivedData.meeting_description
        );
      }
    }
  };

  const authObserver = () => {
    if (sso_auth !== "true" || !sso_auth) {
      onAuthStateChanged(auth, (authUser) => {
        if (disableAuthObserver.current) {
          return;
        }

        const shouldStayOnRegistrationPage = getShouldStayOnCurrentPage();

        if (shouldStayOnRegistrationPage) {
          return;
        }

        if (authUser) {
          const uid = authUser.uid;
          LocalStorage.setAuthenthicationToken(authUser.accessToken);
          LocalStorage.setRefreshToken(authUser.stsTokenManager.refreshToken);

          if (disableAuthCallback.current) {
            return true;
          }

          if (searchParams.get("desktop_login") === "true") {
            getCustomToken(
              (response) => {
                setSearchParasm((prev) => {
                  prev.set("access_token", response.data.authentication_token);
                });
                navigate({
                  pathname: "/desktop_login_success",
                  search: createSearchParams(searchParams).toString(),
                });
              },
              (error) => { }
            );
          } else {
            if (!user) {
              getCurrentUserNoRedirect(
                function () {
                  if (!isElectron && !getShouldPreventRedirect()) {
                    navigate("/");
                  }
                },
                function () { }
              );
            }
          }
        } else {
          if (!getShouldStayOnCurrentPage()) {
            signOutFirebase();
          }
        }
      });
    } else if (sso_auth && sso_auth === "true") {
      const shouldStayOnRegistrationPage = getShouldStayOnCurrentPage();

      if (shouldStayOnRegistrationPage) {
        return;
      }

      getCurrentUserNoRedirect(
        (response) => {
          const userResponse = response.data.user;
          const hasCall = location.pathname.includes("call");

          if (userResponse.language != null && !needsSsoOnboarding(userResponse)) {
            if (hasCall) {
              return;
            }

            if (searchParams.get("desktop_login") === "true") {
              getCustomToken(
                (response) => {
                  setSearchParasm((prev) => {
                    prev.set(
                      "access_token",
                      response.data.authentication_token
                    );
                  });
                  navigate({
                    pathname: "/desktop_login_success",
                    search: createSearchParams(searchParams).toString(),
                  });
                },
                (error) => { }
              );
            } else {
              if (!getShouldPreventRedirect()) {
                navigate("/");
              }
            }
          } else {
            if (!getShouldPreventRedirect()) {
              navigate("/onboarding");
            }
          }
        },
        async (error) => {
          await handleLogoutUser();
          const result = await authBridgeLogin("app");
          if (result?.token && result?.refresh_token) {
            LocalStorage.setAuthenthicationToken(result.token);
            LocalStorage.setRefreshToken(result.refresh_token);
            LocalStorage.setSSOFlag(true);
            getCurrentUserNoRedirect(
              (response) => {
                const userResponse = response.data.user;
                if (userResponse.language != null && !needsSsoOnboarding(userResponse)) {
                  navigate("/");
                } else {
                  navigate("/onboarding");
                }
              },
              () => {}
            );
          }
        }
      );
    }
  };

  const runBridgeAuth = async () => {
    if (getShouldStayOnCurrentPage() || hasLoginForToken) {
      authObserver();
      return;
    }

    const hash = window.location.hash || "";
    const queryIndex = hash.indexOf("?");
    const hashParams = queryIndex !== -1
      ? new URLSearchParams(hash.slice(queryIndex))
      : null;
    const bridgeToken = hashParams?.get("bridge") || null;

    if (bridgeToken) {
      const cleanedHash = hash.slice(0, queryIndex !== -1 ? queryIndex : undefined);
      window.history.replaceState(null, "", window.location.pathname + cleanedHash);

      LocalStorage.removeBridgeOptedOut();

      let result = await authBridgeLogin("app", bridgeToken);

      if (!result?.token) {
        result = await authBridgeLogin("app");
      }

      if (result?.token && result?.refresh_token) {
        LocalStorage.setAuthenthicationToken(result.token);
        LocalStorage.setRefreshToken(result.refresh_token);
        LocalStorage.setSSOFlag(true);

        getCurrentUserNoRedirect(
          (response) => {
            const userResponse = response.data.user;
            if (userResponse.language != null && !needsSsoOnboarding(userResponse)) {
              navigate("/");
            } else {
              navigate("/onboarding");
            }
          },
          () => {
            authObserver();
          }
        );
        return;
      }

      authObserver();
      return;
    }

    const existingToken = LocalStorage.getAuthenticationToken();
    if (!existingToken && !LocalStorage.getBridgeOptedOut()) {
      const result = await authBridgeLogin("app");

      if (result?.token && result?.refresh_token) {
        LocalStorage.setAuthenthicationToken(result.token);
        LocalStorage.setRefreshToken(result.refresh_token);
        LocalStorage.setSSOFlag(true);

        getCurrentUserNoRedirect(
          (response) => {
            const userResponse = response.data.user;
            if (userResponse.language != null && !needsSsoOnboarding(userResponse)) {
              navigate("/");
            } else {
              navigate("/onboarding");
            }
          },
          () => {
            authObserver();
          }
        );
        return;
      }
    }

    authObserver();
  };

  const handleElectronDeeplink = (url) => {
    console.log("Deeplink url", url);
    setDeepLink(null);
    setDeepLink(url);
  };

  useEffect(() => {
    const hash = window.location.hash || "";
    const queryIndex = hash.indexOf("?");
    if (queryIndex !== -1) {
      const params = new URLSearchParams(hash.slice(queryIndex));
      if (params.get("section") === "tools" && params.get("tool")) {
        sessionStorage.setItem("pendingTool", params.get("tool"));
        if (params.get("project_id")) {
          sessionStorage.setItem("pendingToolProjectId", params.get("project_id"));
        }
      }
    }
  }, []);

  useEffect(() => {
    // Ensure `window.electron` is available
    if (isElectron) {
      if (window.electron && typeof window.electron.onDeepLink === "function") {
        window.electron.onDeepLink(handleElectronDeeplink);
        return () => {
          window.electron.onDeepLink(() => { });
        };
      }
    } else {
      console.log("Electron API not available");
    }
  }, []);

  useEffect(() => {
    handleDeeplink();
  }, [deepLink]);

  useEffect(() => {
    handleLinksquaredData();
  }, [linksquaredReceivedData]);

  useEffect(() => {
    ReactGA.set({ user_id: null });
    //Firebase Messaging request
  }, []);

  useEffect(() => {
    if (hasLoginForToken) {
      return;
    }
    runBridgeAuth();
  }, [sso_auth]);

  return (
    <ThemeProvider theme={theme === "light" ? lightTheme : darkTheme}>
      <Wrapper>
        <div id="recaptcha-container"></div>
        <SpinnerComponent isLoading={loading}>
          <Toaster />
          <InitializeAnalytics />
          <CallModal />
          {meetingDataFromDeepLink && (
            <DeeplinkMeetingPickApp
              meetingDataFromDeepLink={meetingDataFromDeepLink}
              setMeetingDataFromDeepLink={setMeetingDataFromDeepLink}
              isElectron={isElectron}
            />
          )}

          <Routes>
            <Route path="*" element={<NotFound />}></Route>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/desktop_login_success"
              element={<DesktopLoginSuccess />}
            />
            <Route path="/sso-authentication" element={<SsoAuthPage />} />
            <Route
              path="/logout-deleted-user"
              element={<LogoutDeletedUser />}
            />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/login-for-token" element={<LoginForTokenPage />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <ChatPage isMeetingOverlayActive={!!meetingDataFromDeepLink} />
                </PrivateRoute>
              }
            ></Route>
            <Route path="/conferences/join" element={<ConferenceJoinPage />} />
            <Route
              path="/conferences/join-in-person"
              element={<ConferenceInPersonJoinPage />}
            />
            <Route
              path="/conferences/in-person-translation"
              element={<ConferenceInPersonTranslationPage />}
            />
            <Route path="/meetings/join" element={<MeetingJoinPage />} />
            <Route
              path="/conferences/:id/registration"
              element={<ConferenceRegistrationPage />}
            />
            <Route
              path="/public/conferences/:id/registration"
              element={<ConferenceRegistrationPage />}
            />
            <Route
              path="/quick-chat/:hashId"
              element={
                <QuickChatContextProvider>
                  <ConversationContextProvider>
                    <QcWebSocketServiceProvider>
                      <QuickChatPage />
                    </QcWebSocketServiceProvider>
                  </ConversationContextProvider>
                </QuickChatContextProvider>
              }
            ></Route>
            <Route path="/call" element={<CallWrapperPage />} />
            <Route
              path="/presentation-translation-tool"
              element={
                <PrivateRoute>
                  <PresentationTranslationToolPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/video-translation-tool"
              element={
                <PrivateRoute>
                  <VideoTranslationToolPage />
                </PrivateRoute>
              }
            />
          </Routes>
        </SpinnerComponent>
        {!isOnline && <OfflineView />}
      </Wrapper>
    </ThemeProvider>
  );
}

export default App;
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;
